<script setup>
import { ref, computed, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import DealsApi from 'dashboard/api/dealsApi';

const POLL_INTERVAL = 5000;
const { t } = useI18n();

const status = ref(null);
const processedRecords = ref(null);
const pollTimer = ref(null);

const isVisible = computed(
  () => status.value === 'pending' || status.value === 'processing'
);
const isCompleted = computed(() => status.value === 'completed');
const isFailed = computed(() => status.value === 'failed');
const showBanner = computed(
  () => isVisible.value || isCompleted.value || isFailed.value
);

const stopPolling = () => {
  if (pollTimer.value) {
    clearInterval(pollTimer.value);
    pollTimer.value = null;
  }
};

const fetchStatus = async () => {
  try {
    const { data } = await DealsApi.importStatus();
    status.value = data.status;
    processedRecords.value = data.processed_records;
    if (data.status === 'completed' || data.status === 'failed') stopPolling();
  } catch {
    stopPolling();
  }
};

const dismiss = () => {
  stopPolling();
  status.value = null;
};

const startPolling = async () => {
  await fetchStatus();
  if (status.value === 'pending' || status.value === 'processing') {
    pollTimer.value = setInterval(fetchStatus, POLL_INTERVAL);
  }
};

onUnmounted(stopPolling);
defineExpose({ startPolling, dismiss });
</script>

<template>
  <Transition name="banner">
    <div
      v-if="showBanner"
      class="flex items-center justify-between gap-3 rounded-lg px-4 py-2.5 text-sm"
      :class="{
        'bg-n-blue-3 text-n-blue-11': isVisible,
        'bg-n-teal-3 text-n-teal-11': isCompleted,
        'bg-n-red-3 text-n-red-11': isFailed,
      }"
    >
      <div class="flex items-center gap-2">
        <span
          v-if="isVisible"
          class="i-lucide-loader-circle animate-spin size-4 shrink-0"
        />
        <span
          v-else-if="isCompleted"
          class="i-lucide-circle-check size-4 shrink-0"
        />
        <span v-else class="i-lucide-circle-x size-4 shrink-0" />

        <span v-if="status === 'pending'">
          {{ t('DEALS_LAYOUT.IMPORT_STATUS.PENDING') }}
        </span>
        <span v-else-if="status === 'processing'">
          {{ t('DEALS_LAYOUT.IMPORT_STATUS.PROCESSING') }}
        </span>
        <span v-else-if="isCompleted">
          {{
            t('DEALS_LAYOUT.IMPORT_STATUS.COMPLETED', {
              count: processedRecords ?? 0,
            })
          }}
        </span>
        <span v-else>
          {{ t('DEALS_LAYOUT.IMPORT_STATUS.FAILED') }}
        </span>
      </div>

      <button
        class="i-lucide-x size-4 shrink-0 opacity-60 hover:opacity-100 transition-opacity"
        @click="dismiss"
      />
    </div>
  </Transition>
</template>
